import * as React from 'react';

import {
  Badge,
  Button,
  FloatingToolbar,
  Row,
  useMemoDataGridRows,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucideBanknoteArrowUp, LucideTrash2, LucideX } from 'lucide-react';

import { invoiceVoidAtom } from '../../_graphql/mutationInvoiceVoid';
import * as state from '../../_state/financeInvoices.state';

import { useTranslations } from '@/finance/i18n/useTranslations';
import { InvoiceRecord } from '~/modules/finance/types';

//
// ⚛️ ⚛️ ⚛️ INVOICES LIST FLOATING TOOLBAR ⚛️ ⚛️ ⚛️
//
export const InvoicesListFloatingToolbar = React.memo(() => {
  // Translations
  const t = useTranslations();

  // Atoms
  const { data: { invoices } = { invoices: [] }, refetch: refetchInvoices } =
    useAtomValue(state.$remoteInvoices);
  const [selectedKeys, setSelectedKeys] = useAtom(state.$selectedKeys);
  const setInvoicesToReceive = useSetAtom(state.$invoicesToReceive);
  const setIsPaymentDrawerOpen = useSetAtom(state.$isPaymentDrawerOpen);

  // Memoized values
  const $voidInvoice = React.useMemo(
    () =>
      invoiceVoidAtom({
        onSuccess: () => {
          refetchInvoices?.();
          setSelectedKeys(new Set());
        },
      }),
    [refetchInvoices, setSelectedKeys],
  );
  const voidInvoice = useSetAtom($voidInvoice);

  // Data hooks
  const toast = useToast();
  const selectedRows = useMemoDataGridRows(invoices, selectedKeys);

  // Callbacks
  const handleReceive = React.useCallback(
    (invoicesToPay: InvoiceRecord[]) => {
      const allPending = invoicesToPay.every(
        (invoice) => invoice.status === 'Pending',
      );
      if (!allPending) {
        toast.error(t.invoices.list.tooltips.notAllPending);
        return;
      }
      setInvoicesToReceive(invoicesToPay);
      setIsPaymentDrawerOpen(true);
    },
    [toast, t, setInvoicesToReceive, setIsPaymentDrawerOpen],
  );

  const handleBatchVoid = React.useCallback(
    async (invoicesToVoid: InvoiceRecord[]) => {
      const canVoid = invoicesToVoid.every(
        (inv) => inv.status === 'Pending' || inv.status === 'ProForma',
      );
      if (!canVoid) {
        toast.error(t.invoices.list.tooltips.cannotVoid);
        return;
      }
      let succeeded = 0;
      for (const invoice of invoicesToVoid) {
        try {
          await voidInvoice({ id: invoice.id });
          succeeded++;
        } catch (e) {
          console.error(e);
        }
      }
      if (succeeded === invoicesToVoid.length) {
        toast.success(t.invoices.list.toolbar.voidSuccess(succeeded));
      } else if (succeeded > 0) {
        toast.warning(
          t.invoices.list.toolbar.voidPartial(succeeded, invoicesToVoid.length),
        );
      } else {
        toast.error(t.invoices.list.toolbar.voidFailed);
      }
    },
    [voidInvoice, toast, t],
  );

  return (
    <FloatingToolbar visible={selectedRows.length > 0}>
      <Row xBetween gap3>
        <Row gap2 className="text-sm">
          <Badge color="primary" size="small">
            {selectedRows.length}
          </Badge>
          {t.invoices.list.toolbar.selected}
        </Row>
        <Row gap2>
          <Button
            variant="solid"
            color="primary"
            icon={<LucideBanknoteArrowUp className="size-4" />}
            onPress={() => handleReceive(selectedRows)}
          >
            {t.invoices.list.toolbar.receive(selectedRows.length)}
          </Button>
          <Button
            variant="outline"
            color="failure"
            icon={<LucideTrash2 className="size-4" />}
            onPress={() => handleBatchVoid(selectedRows)}
          >
            {t.invoices.list.toolbar.void(selectedRows.length)}
          </Button>
        </Row>
        <LucideX
          className="size-4 cursor-pointer text-neutral/80"
          onClick={() => setSelectedKeys(new Set())}
        />
      </Row>
    </FloatingToolbar>
  );
});
